import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginService,
  sendOtp,
  signupService,
  verifyOtp,
} from "../../apis/auth";
import { LoginFormInputs } from "../../pages/auth/auth_types";
import { toast } from "react-toastify";
import { User } from "../../pages/private/admin-management/types";
import { signupFormData, Tokens } from "./types/authTypes";
import { store } from "../app/store";

export const acceptedRoles = ["MARKETER"];

interface AuthState {
  user: {
    tokens: Tokens;
    user: User;
  } | null;
  isAuthenticated: boolean;
  authenticatingUser: boolean;
  error: string | null;
  unAuthorizedForgotPassword: {
    email: string;
    hash: string;
    otp: string;
  };
  signupFormData: signupFormData;
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem("user") ?? "{}") ?? null,
  isAuthenticated: false,
  // isAuthenticated: !!localStorage.getItem("user"),
  authenticatingUser: false,
  error: null,
  unAuthorizedForgotPassword: {
    email: "",
    hash: "",
    otp: "",
  },

  signupFormData: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    country: "",
    language: "",
    hash: "",
  },
};

// Async thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }: LoginFormInputs, { rejectWithValue }) => {
    try {
      const data = await loginService({ email, password });
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Login failed");
      return rejectWithValue(error.response?.data?.message ?? "Login failed");
    }
  }
);

export const sendOtpThunk = createAsyncThunk(
  "auth/sendOtp",
  async (payload: { email: string }, { rejectWithValue }) => {
    try {
      const data = await sendOtp(payload);
      toast.success(data?.message || "OTP sent successfully");
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Failed to send OTP");
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to send OTP"
      );
    }
  }
);

export const verifyOtpThunk = createAsyncThunk(
  "auth/verifyOtp",
  async (
    payload: { email: string; otp: string; hash: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await verifyOtp(payload);
      toast.success(data?.message || "OTP verified successfully");
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "OTP verification failed");
      return rejectWithValue(
        error.response?.data?.message ?? "OTP verification failed"
      );
    }
  }
);

// Async thunk for signup
export const signup = createAsyncThunk(
  "auth/signup",
  async (signUpData: any, { rejectWithValue }) => {
    try {
      const data = await signupService(signUpData);
      toast.success("Signup successful");
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Signup failed");
      return rejectWithValue(error.response?.data?.message ?? "Signup failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLogin: (state) => {
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.authenticatingUser = false;
      state.error = null;
      localStorage.clear(); // Remove from localStorage on logout
    },
    updateUnAuthorizedForgotPassword: (state, action: PayloadAction<any>) => {
      state.unAuthorizedForgotPassword = {
        ...state.unAuthorizedForgotPassword,
        [action.payload.name]: action.payload.value,
      };
    },
    resetUnAuthorizedForgotPassword: (state) => {
      state.unAuthorizedForgotPassword = {
        email: "",
        hash: "",
        otp: "",
      };
    },
    setSignupFormData: (state, action: PayloadAction<any>) => {
      state.signupFormData = {
        ...state.signupFormData,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    // Login reducers
    builder
      .addCase(login.pending, (state) => {
        state.authenticatingUser = true;
        state.error = null;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<{ tokens: Tokens; user: User }>) => {
          state.authenticatingUser = false;
          const { user }: any = action.payload;
          if (acceptedRoles.includes(user.roles)) {
            toast.success("Login successful");
            state.user = action.payload;
            state.isAuthenticated = true;
            localStorage.setItem("user", JSON.stringify(action.payload));
          } else {
            toast.error("You are not authorized to access this application");
          }
        }
      )
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });

    // Signup reducers
    builder
      .addCase(sendOtpThunk.pending, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = true;

        state.error = null;
      })
      .addCase(sendOtpThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
      })
      .addCase(sendOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });
    // Signup reducers
    builder
      .addCase(verifyOtpThunk.pending, (state) => {
        state.authenticatingUser = true;
        state.error = null;
      })
      .addCase(verifyOtpThunk.fulfilled, (state) => {
        state.authenticatingUser = false;
        state.signupFormData = initialState.signupFormData;
      })
      .addCase(verifyOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });
    // Signup reducers
    builder
      .addCase(signup.pending, (state) => {
        state.authenticatingUser = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.authenticatingUser = false;
        state.signupFormData = {
          ...state.signupFormData,
          hash: action.payload?.hash || "",
        };
      })
      .addCase(signup.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });
  },
});

export const {
  logout,
  updateUnAuthorizedForgotPassword,
  resetUnAuthorizedForgotPassword,
  setLogin,
  setSignupFormData,
} = authSlice.actions;
export default authSlice.reducer;
